import { useMemo, useState } from "react";

import { useInterviewLiveStore } from "../store.ts";

// Custom questions the interviewer wrote (manual) come first, then AI-added,
// then the standard bank questions (master).
const ORIGIN_ORDER: Record<string, number> = { manual: 0, ai: 1, master: 2 };

/** Interactive checklist of planned questions + AI-generated suggestions. */
export function QuestionChecklist() {
  const checklist = useInterviewLiveStore((s) => s.checklist);
  const toggleAsked = useInterviewLiveStore((s) => s.toggleAsked);
  const generated = useInterviewLiveStore((s) => s.generatedQuestions);
  const addChecklistItem = useInterviewLiveStore((s) => s.addChecklistItem);
  const [custom, setCustom] = useState("");

  // Stable sort: custom/AI questions on top, preserving insertion order within
  // each group.
  const ordered = useMemo(
    () =>
      checklist
        .map((c, i) => ({ c, i }))
        .sort(
          (a, b) => (ORIGIN_ORDER[a.c.origin] ?? 3) - (ORIGIN_ORDER[b.c.origin] ?? 3) || a.i - b.i,
        )
        .map(({ c }) => c),
    [checklist],
  );

  return (
    <div style={{ padding: "8px 12px" }}>
      <ul style={{ listStyle: "none", padding: 0, margin: 0 }}>
        {ordered.map((c) => (
          <li
            key={c.id}
            style={{ display: "flex", gap: 8, alignItems: "flex-start", padding: "6px 0" }}
          >
            <input type="checkbox" checked={c.asked} onChange={() => toggleAsked(c.id)} />
            <span
              style={{
                fontSize: 13,
                textDecoration: c.asked ? "line-through" : "none",
                color: c.asked ? "var(--text-4)" : "var(--text-1)",
              }}
            >
              {c.text}
              {c.origin !== "master" && (
                <em style={{ marginLeft: 6, fontSize: 10, color: "#7627bb" }}>
                  {c.origin === "manual" ? "custom" : "AI"}
                </em>
              )}
            </span>
          </li>
        ))}
      </ul>

      <div style={{ display: "flex", gap: 6, marginTop: 10 }}>
        <input
          style={{
            flex: 1,
            padding: "6px 8px",
            borderRadius: 6,
            border: "1px solid var(--border-1)",
            background: "var(--surface-2)",
            color: "var(--text-1)",
            fontSize: 13,
          }}
          value={custom}
          onChange={(e) => setCustom(e.target.value)}
          placeholder="Add a question…"
        />
        <button
          type="button"
          className="pill-btn"
          onClick={() => {
            const t = custom.trim();
            if (!t) return;
            addChecklistItem(t, "manual");
            setCustom("");
          }}
        >
          Add
        </button>
      </div>

      {generated.length > 0 && (
        <div style={{ marginTop: 20 }}>
          <h4 style={{ margin: "0 0 6px", color: "#7627bb" }}>AI suggestions</h4>
          {generated.map((q, i) => (
            <div
              key={`${i}-${q.slice(0, 12)}`}
              style={{ display: "flex", gap: 8, alignItems: "flex-start", padding: "4px 0" }}
            >
              <span style={{ fontSize: 13, flex: 1, color: "var(--text-2)" }}>{q}</span>
              <button
                type="button"
                className="icon-btn"
                title="Add to checklist"
                onClick={() => addChecklistItem(q, "ai")}
              >
                +
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
